import { sortByKey } from '@semios/app-platform-common'
import type {
  EVENT_ID,
  ISOString,
  TIrrigationSchedulerStoreState,
} from 'App/irrigation-scheduler/utils/store/irrigationSchedulerStore'
import {
  getInitialState,
  irrigationSchedulerStore,
} from 'App/irrigation-scheduler/utils/store/irrigationSchedulerStore'
import type { TIrrigationDeviceStatus } from 'App/Map/types'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TValidIrrigationSchedulerZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { irrigationSchedulerFertigationSettingsGet } from '../../api/irrigationSchedulerFertigationEventsGet'
import { getIrrigationProperties } from '../selectors/getIrrigationProperties'
import type { TZoneWithETDataArray } from '../types'
import { eventsCreate } from './eventsCreate'
import { eventsGet } from './eventsGet'
import { eventsUpdateAndOrDelete } from './eventsUpdateAndOrDelete'
import { fetchAllIrrigationSchedulerEventsAndCurrentValuesData } from './fetchAllIrrigationSchedulerEventsAndCurrentValuesData'
import { fetchFertigationSettings } from './fetchFertigationSettings'
import { pulseEventsCreate } from './pulseEventsCreate'
import { pulseEventsDelete } from './pulseEventsDelete'
import { pulseEventsUpdate } from './pulseEventsUpdate'
import { selectScheduledEventsForBatchDelete } from './selectScheduledEventsForBatchDelete'
import { setDateView } from './setDateView'
import { setSelectedPropertyIdAndClearSelectedIrrigationZoneEmitterIds } from './setSelectedPropertyIdAndClearSelectedIrrigationZoneEmitterIds'
import { toggleEventForBatchDelete } from './toggleEventForBatchDelete'
import { togglePulseEventForBatchDelete } from './togglePulseEventForBatchDelete'
import { updateMostRecentApiResponseTimestamp } from './updateMostRecentApiResponseTimestamp'

const closeRightPanel: Pick<
  TIrrigationSchedulerStoreState,
  'eventCreate' | 'eventPanel' | 'pulseEventPanel' | 'showPublishScheduleConfirmation'
> = {
  eventCreate: null,
  eventPanel: null,
  pulseEventPanel: null,
  showPublishScheduleConfirmation: false,
}

export const actions = {
  main: {
    openIrrigationScheduler: () => {
      const irrigationProperties = getIrrigationProperties(fieldAssetStore.getState()).sort(
        sortByKey('propertyName'),
      )

      if (irrigationProperties.length === 0) {
        throw new Error('No irrigation properties found')
      }

      const selectedPropertyId = selectedFieldAssetsStore.getState().property

      const selectedProperty = irrigationProperties.find(
        (property) => property.propertyId === selectedPropertyId,
      )

      const targetProperty = selectedProperty || irrigationProperties[0]

      irrigationSchedulerStore.setState((state) => ({
        ...state,
        dateFrom: moment.tz(targetProperty.timezone).startOf('week').toISOString(),
        selectedPropertyId: targetProperty.propertyId,
        visible: true,
      }))
    },
    closeIrrigationScheduler: () => {
      irrigationSchedulerStore.setState((s) => ({
        ...getInitialState(),
        show3rdPartyPublishedEvents: s.show3rdPartyPublishedEvents,
        showTimeOnBars: s.showTimeOnBars,
        showPlanningData: s.showPlanningData,
      }))
    },
    closeRightPanel: () => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        ...closeRightPanel,
      }))
    },
    setShowLauncher: (showLauncher: boolean) => {
      irrigationSchedulerStore.setState((s) => ({ ...s, showLauncher }))
    },
    setSelectedPropertyIdAndClearSelectedIrrigationZoneEmitterIds,
    setSelectedIrrigationZoneEmitterIds: (
      irrigationZoneEmitterIds: TValidIrrigationSchedulerZoneEmitterId[],
    ) => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        selectedIrrigationZoneEmitterIds: irrigationZoneEmitterIds,
      }))
    },
    showPeakHours: (show: boolean) => {
      irrigationSchedulerStore.setState((s) => ({ ...s, showPeakHours: show }))
    },
    setDateFrom: (fn: (s: TIrrigationSchedulerStoreState) => ISOString) => {
      irrigationSchedulerStore.setState((s) => {
        return { ...s, dateFrom: fn(s) }
      })
    },
    setStoppedIrrigationZoneEmitters: (
      items: Array<{
        irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId
        timestamp: ISOString
      }>,
    ) => {
      irrigationSchedulerStore.setState((s) => {
        return {
          ...s,
          stoppedIrrigationZoneEmitters: items.reduce(
            (acc, { irrigationZoneEmitterId, timestamp }) => ({
              ...acc,
              [irrigationZoneEmitterId]: timestamp,
            }),
            {},
          ),
        }
      })
    },
    setEmitterStatus: (
      items: Array<{
        irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId
        currentEmitterStatus: TIrrigationDeviceStatus
      }>,
    ) => {
      irrigationSchedulerStore.setState((s) => {
        return {
          ...s,
          currentEmitterStatus: items.reduce(
            (acc, { irrigationZoneEmitterId, currentEmitterStatus }) => ({
              ...acc,
              [irrigationZoneEmitterId]: currentEmitterStatus,
            }),
            {},
          ),
        }
      })
    },
    togglePlanningDataView: () => {
      // hack: Fixes a white space bug caused by toggling planning data on/off
      document.querySelectorAll<HTMLElement>('.highcharts-tooltip-container').forEach((el) => {
        el.style.left = '-9999px'
      })

      irrigationSchedulerStore.setState((s) => ({
        ...s,
        showPlanningData: !s.showPlanningData,
      }))
    },
    setIrrigationPlanningData: (data: { dailyEt: TZoneWithETDataArray[]; lastFetchedDateKey: string }) => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        irrigationPlanningData: {
          ...data,
          loading: false,
          error: null,
          hasFetched: true,
        },
      }))
    },
    clearIrrigationPlanningDataCache: () => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        irrigationPlanningData: s.irrigationPlanningData
          ? {
              ...s.irrigationPlanningData,
              lastFetchedDateKey: null,
            }
          : null,
      }))
    },
  },
  scheduledEventPanel: {
    openScheduledEventFormCreate: () => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        ...closeRightPanel,
        eventCreate: {},
      }))
    },
    openEventPanel: ({ eventId }: { eventId: EVENT_ID }) => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        ...closeRightPanel,
        eventPanel: { eventId },
      }))
    },
    openPulseEventPanel: ({ pulseEventId }: { pulseEventId: EVENT_ID }) => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        ...closeRightPanel,
        pulseEventPanel: { pulseEventId },
      }))
    },
  },
  publishPanel: {
    open: () => irrigationSchedulerStore.setState((s) => ({ ...s, showPublishScheduleConfirmation: true })),
  },
  batchDeleteMode: {
    deselectAllEventsMarkedForBatchDelete: () => {
      irrigationSchedulerStore.setState((s) => {
        return {
          ...s,
          batchDelete: {
            ...s.batchDelete,
            events: {},
          },
        }
      })
    },
    openBatchDeleteMode: () => {
      irrigationSchedulerStore.setState((s) => ({ ...s, batchDelete: { visible: true, events: {} } }))
    },
    closeBatchDeleteMode: () => {
      irrigationSchedulerStore.setState((s) => ({ ...s, batchDelete: { visible: false, events: {} } }))
    },
    selectScheduledEventsForBatchDelete,
    toggleEventForBatchDelete,
    togglePulseEventForBatchDelete,
  },
  api: {
    eventsCreate,
    eventsGet,
    eventsUpdateAndOrDelete,
    fetchFertigationSettings,
    irrigationSchedulerFertigationSettingsGet,
    pulseEventsCreate,
    pulseEventsUpdate,
    pulseEventsDelete,
    updateMostRecentApiResponseTimestamp,
    fetchAllIrrigationSchedulerEventsAndCurrentValuesData,
    setFertigationSettings: (fertigationSettings: TIrrigationSchedulerStoreState['fertigationSettings']) => {
      irrigationSchedulerStore.setState((s) => {
        return {
          ...s,
          fertigationSettings,
        }
      })
    },
  },
  advanced: {
    setItem: (
      item: 'show3rdPartyPublishedEvents' | 'showTimeOnBars' | 'showPlanningData',
      enable: boolean,
    ) => {
      irrigationSchedulerStore.setState((s) => ({
        ...s,
        [item]: enable,
      }))
    },
  },
  settings: {
    setDateView,
  },
}
